// CTV-466 follow-up: shared Pattern A primary CTA. Gradient pill themed to
// the station accent (or a neutral slate gradient when no station context is
// available, e.g. ResetPasswordPage, the AdvertiserCreativeLibraryPage
// empty-state sign-in). Shape, hover, and shadow are identical across both
// color variants so the only thing that changes is the brand color.
//
// CTV-775: `variant="hero"` adds the landing page Get Started treatment
// (shimmer sweep + hover lift + deeper shadow) for the one action on a
// surface we want clicked: the Waymark panel's primary CTAs and the
// disclosure row's action pill. `shimmer` picks continuous ("always", the
// ready-to-run moment) or hover-only ("hover", quiet hero). The default
// variant is byte-for-byte what it was before CTV-775.

import React from 'react';
import { tintFromAccent } from '../steps/_shared';

interface PrimaryButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  // Station accent color. When omitted, falls back to a neutral slate gradient
  // (used on pre-auth surfaces that don't carry a station config).
  accentColor?: string;
  // Deeper hue used as the gradient endpoint + shadow color. Defaults to
  // accentColor when absent.
  primaryColor?: string;
  size?: 'sm' | 'md';
  fullWidth?: boolean;
  // CTV-775: 'hero' = shimmer sweep + hover lift. Default is the plain pill.
  variant?: 'default' | 'hero';
  // CTV-775: hero shimmer cadence. 'always' loops a 5.5s sweep; 'hover' runs
  // one sweep per hover. Ignored on the default variant. Defaults to 'hover'.
  shimmer?: 'always' | 'hover';
  // CTV-775: white pill with the primary color as text, for use ON a
  // gradient (the disclosure row). Only meaningful with variant="hero".
  inverse?: boolean;
  children: React.ReactNode;
}

const NEUTRAL_ACCENT = '#475569';
const NEUTRAL_PRIMARY = '#1e293b';

// One <style> for the hero keyframes, injected on first hero render. Kept in
// a module-level guard so a page with many hero buttons carries one copy.
const HERO_STYLE_ID = 'ctv-primary-button-hero-styles';
const HERO_CSS = `
@keyframes ctvHeroSheen { 0% { transform: translateX(-100%); } 60% { transform: translateX(100%); } 100% { transform: translateX(100%); } }
.ctv-hero-btn { transition: transform .18s ease, filter .18s ease, box-shadow .18s ease; }
.ctv-hero-btn:hover:not(:disabled) { transform: translateY(-1px); }
.ctv-hero-btn .ctv-hero-sheen { position: absolute; inset: 0; transform: translateX(-100%); pointer-events: none; }
.ctv-hero-btn[data-shimmer="always"] .ctv-hero-sheen { animation: ctvHeroSheen 5.5s ease-in-out infinite; }
.ctv-hero-btn[data-shimmer="hover"]:hover:not(:disabled) .ctv-hero-sheen { animation: ctvHeroSheen 1.1s ease-out 1; }
@media (prefers-reduced-motion: reduce) {
  .ctv-hero-btn, .ctv-hero-btn .ctv-hero-sheen { animation: none !important; transition: none; }
  .ctv-hero-btn:hover:not(:disabled) { transform: none; }
}
`;

function ensureHeroStyles(): void {
  if (typeof document === 'undefined') return;
  if (document.getElementById(HERO_STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = HERO_STYLE_ID;
  style.textContent = HERO_CSS;
  document.head.appendChild(style);
}

const PrimaryButton: React.FC<PrimaryButtonProps> = ({
  accentColor,
  primaryColor,
  size = 'md',
  fullWidth = false,
  variant = 'default',
  shimmer = 'hover',
  inverse = false,
  className = '',
  children,
  ...rest
}) => {
  const a = accentColor || NEUTRAL_ACCENT;
  const p = primaryColor || (accentColor ?? NEUTRAL_PRIMARY);
  const pad = size === 'sm' ? 'px-4 py-1.5 text-[12px]' : 'px-5 py-2 text-[13px]';

  if (variant !== 'hero') {
    return (
      <button
        {...rest}
        className={`inline-flex items-center justify-center gap-1.5 ${pad} ${fullWidth ? 'w-full' : ''} rounded-full font-semibold text-white transition-all hover:brightness-110 active:scale-[0.98] disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:brightness-100 ${className}`}
        style={{
          background: `linear-gradient(180deg, ${a}, ${p})`,
          boxShadow: `0 1px 0 rgba(255,255,255,0.25) inset, 0 6px 14px -8px ${p}`,
        }}
      >
        {children}
      </button>
    );
  }

  ensureHeroStyles();
  const sheenBackground = inverse
    ? `linear-gradient(120deg, transparent 30%, ${tintFromAccent(a, 0.22)} 50%, transparent 70%)`
    : 'linear-gradient(120deg, transparent 30%, rgba(255,255,255,0.4) 50%, transparent 70%)';
  return (
    <button
      {...rest}
      data-variant="hero"
      data-shimmer={shimmer}
      className={`ctv-hero-btn relative overflow-hidden inline-flex items-center justify-center gap-1.5 ${pad} ${fullWidth ? 'w-full' : ''} rounded-full font-semibold hover:brightness-110 active:scale-[0.98] disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:brightness-100 ${inverse ? '' : 'text-white'} ${className}`}
      style={
        inverse
          ? {
              color: p,
              background: '#ffffff',
              boxShadow: '0 8px 20px -10px rgba(0,0,0,0.45)',
            }
          : {
              background: `linear-gradient(180deg, ${a}, ${p})`,
              boxShadow: `0 1px 0 rgba(255,255,255,0.3) inset, 0 0 0 1px rgba(255,255,255,0.12) inset, 0 10px 22px -10px ${p}, 0 4px 10px -6px ${tintFromAccent(a, 0.6)}`,
            }
      }
    >
      <span aria-hidden className="ctv-hero-sheen" style={{ background: sheenBackground }} />
      <span className="relative inline-flex items-center gap-1.5">{children}</span>
    </button>
  );
};

export default PrimaryButton;
